import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, Lock, Server, Users, TrendingUp, Zap, ArrowRight,
  CheckCircle2, FileText, Database, Clock, Globe, Award,
} from "lucide-react";

export default function Enterprise() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-14 flex items-center justify-between">
          <Link to="/" className="font-display font-black text-lg tracking-tight">
            Rule<span className="text-primary">AI</span><span className="text-muted-foreground">.systems</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <Link to="/enterprise" className="text-primary font-medium">Enterprise</Link>
            <Link to="/security" className="hover:text-primary">Security</Link>
            <Link to="/library" className="hover:text-primary">Library</Link>
            <Link to="/about" className="hover:text-primary">Company</Link>
            <Link to="/contact"><Button size="sm" className="rounded-sm bg-primary hover:bg-primary/90" data-testid="enterprise-nav-contact">Contact sales</Button></Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="overline text-primary mb-4">/ ENTERPRISE</div>
              <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight leading-[1.05]" data-testid="enterprise-hero">
                An AI-native Business OS for mid-market manufacturers &amp; industrial operators.
              </h1>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-2xl">
                RuleAI.Systems unifies SOPs, quality management, HR, operations, and analytics into a single AI-native workspace &mdash; replacing eight or more disconnected tools with one auditable source of truth.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/contact">
                  <Button size="lg" className="rounded-sm bg-primary hover:bg-primary/90 h-12 px-6" data-testid="enterprise-cta-contact">
                    Talk to sales <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/security">
                  <Button size="lg" variant="outline" className="rounded-sm h-12 px-6" data-testid="enterprise-cta-security">
                    Review our security posture
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { k: "88%", v: "Faster SOP authoring" },
                  { k: "8→1", v: "Tools consolidated" },
                  { k: "$16k", v: "Avg. annual savings" },
                  { k: "60s", v: "Workspace provisioning" },
                ].map((m) => (
                  <div key={m.k} className="border border-border bg-card p-6 rounded-sm">
                    <div className="font-display font-black text-3xl tracking-tight">{m.k}</div>
                    <div className="overline text-muted-foreground mt-2">{m.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why enterprise */}
      <section className="border-b border-border bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="max-w-3xl mb-12">
            <div className="overline text-primary mb-3">/ WHY ENTERPRISE OPERATIONS TEAMS CHOOSE RULEAI</div>
            <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
              Purpose-built for regulated, multi-site industrial operations.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: ShieldCheck, title: "Audit-ready by design",
                desc: "Every SOP, record, and approval carries an immutable activity log. ISO 9001, IATF 16949, ISO 13485, and FDA-friendly workflows built in." },
              { icon: Users, title: "Multi-tenant RBAC",
                desc: "Workspace-level isolation. Owner / Admin / Editor / Viewer roles. Super-admin console for platform ops. No data crosses org boundaries." },
              { icon: Zap, title: "Native AI copilots",
                desc: "Claude Sonnet 4.6 baked into SOP drafting, technical sales analysis, and marketing. Encrypted key management via our Universal LLM Gateway." },
              { icon: Database, title: "Shared source of truth",
                desc: "One workspace replaces Notion, Trainual, Monday, MasterControl, BambooHR, Canva, Miro, and ChatGPT Team &mdash; with all data linked, not siloed." },
              { icon: Server, title: "Enterprise deployment",
                desc: "Cloud-hosted with dedicated instance option. Custom subdomain, SSO on the roadmap, and volume-based enterprise agreements." },
              { icon: TrendingUp, title: "Operating leverage",
                desc: "Ops teams report 88% faster SOP authoring, 8-tool consolidation, and $16k+ annual savings vs. incumbent stack." },
            ].map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="border border-border bg-card p-6 rounded-sm">
                  <div className="w-10 h-10 rounded-sm bg-primary/10 grid place-items-center">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="font-display font-bold text-lg mt-4">{f.title}</div>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industry verticals */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="overline text-primary mb-3">/ INDUSTRY VERTICALS</div>
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight max-w-3xl">
            Built for regulated industrial operators.
          </h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              "Manufacturing", "Printing & Converting", "Medical Devices", "Aerospace",
              "Automotive", "Food & Beverage", "Warehousing & Logistics", "Chemical & Coatings",
              "Packaging", "Construction", "Field Services", "Industrial Distribution",
            ].map((v) => (
              <div key={v} className="border border-border bg-card px-5 py-4 rounded-sm text-sm font-medium">
                {v}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deployment tiers */}
      <section className="border-b border-border bg-zinc-950 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="max-w-3xl mb-12">
            <div className="overline text-accent mb-3">/ ENTERPRISE PROCUREMENT</div>
            <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
              Volume &amp; multi-site pricing tailored to your operation.
            </h2>
            <p className="text-zinc-400 mt-4 max-w-2xl">
              Standard SaaS pricing published on our site is designed for SMB. Enterprise agreements include volume discounts, procurement paperwork, annual invoicing, and success-team support.
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-4">
            {[
              { name: "Growth", seats: "Up to 25 seats", features: ["All modules", "AI copilots (Claude Sonnet 4.6)", "Email support (24-hr)", "99.5% uptime target"] },
              { name: "Business", seats: "25–250 seats", features: ["Everything in Growth", "Single sign-on (roadmap)", "Custom subdomain", "Quarterly success review", "Priority support"] },
              { name: "Enterprise", seats: "250+ seats · Multi-site", features: ["Everything in Business", "Dedicated instance option", "Custom SLA & MSA", "Named account manager", "Volume LLM pricing"] },
            ].map((tier) => (
              <div key={tier.name} className="border border-zinc-800 bg-zinc-900/60 p-6 rounded-sm">
                <div className="overline text-zinc-500">{tier.seats}</div>
                <div className="font-display font-black text-2xl mt-2">{tier.name}</div>
                <ul className="mt-4 space-y-2">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                      <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/contact">
              <Button size="lg" className="rounded-sm bg-accent hover:bg-accent/90 text-white h-12 px-8" data-testid="enterprise-cta-quote">
                Request enterprise quote <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust signals */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="grid lg:grid-cols-3 gap-6">
            <TrustCard icon={Lock} title="Encryption in transit &amp; at rest"
              body="TLS 1.2+ for all traffic. Data encrypted at rest via MongoDB Atlas AES-256 (production)." />
            <TrustCard icon={FileText} title="Immutable audit trail"
              body="Every SOP action, record change, approval, and comment is logged with actor, timestamp, and before/after state." />
            <TrustCard icon={Clock} title="Backup &amp; disaster recovery"
              body="Point-in-time recovery. Daily snapshots retained. Recovery point objective ≤ 24 hours." />
          </div>
          <div className="mt-10 text-center">
            <Link to="/security" className="text-sm text-primary hover:underline font-mono-plex uppercase tracking-widest inline-flex items-center gap-2">
              Full security &amp; compliance detail <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-zinc-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20 text-center">
          <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight">
            Ready to consolidate your ops stack?
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            Our founders review every enterprise request personally. Bring your current tool stack; we&rsquo;ll show you exactly how a consolidation looks &mdash; ROI, migration, and rollout plan &mdash; on the first call.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/contact">
              <Button size="lg" className="rounded-sm bg-primary hover:bg-primary/90 h-12 px-8" data-testid="enterprise-cta-final">
                Contact sales <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="mailto:support@ruleai.systems">
              <Button size="lg" variant="outline" className="rounded-sm h-12 px-8">
                Email us
              </Button>
            </a>
          </div>
        </div>
      </section>

      <EnterpriseFooter />
    </div>
  );
}

function TrustCard({ icon: Icon, title, body }) {
  return (
    <div className="border border-border bg-card p-6 rounded-sm">
      <div className="w-10 h-10 rounded-sm bg-emerald-500/10 grid place-items-center">
        <Icon className="w-5 h-5 text-emerald-600" />
      </div>
      <div className="font-display font-bold text-lg mt-4" dangerouslySetInnerHTML={{ __html: title }} />
      <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{body}</p>
    </div>
  );
}

export function EnterpriseFooter() {
  return (
    <footer className="border-t border-border bg-card">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="grid md:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="font-display font-black text-lg tracking-tight mb-4">
              Rule<span className="text-primary">AI</span><span className="text-muted-foreground">.systems</span>
            </div>
            <p className="text-muted-foreground text-xs leading-relaxed">
              AI-native Business OS for manufacturing, quality, HR, and operations teams.
            </p>
          </div>
          <div>
            <div className="overline text-muted-foreground mb-3">Product</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><Link to="/" className="hover:text-primary">Overview</Link></li>
              <li><Link to="/library" className="hover:text-primary">Library</Link></li>
              <li><Link to="/#platform" className="hover:text-primary">Platform</Link></li>
              <li><Link to="/#pricing" className="hover:text-primary">Pricing</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline text-muted-foreground mb-3">Company</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><Link to="/about" className="hover:text-primary">About</Link></li>
              <li><Link to="/enterprise" className="hover:text-primary">Enterprise</Link></li>
              <li><Link to="/investors" className="hover:text-primary">Investors</Link></li>
              <li><Link to="/contact" className="hover:text-primary">Contact</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline text-muted-foreground mb-3">Trust &amp; Legal</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><Link to="/security" className="hover:text-primary">Security &amp; Compliance</Link></li>
              <li><Link to="/privacy" className="hover:text-primary">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-primary">Terms of Service</Link></li>
              <li><a href="mailto:support@ruleai.systems" className="hover:text-primary">support@ruleai.systems</a></li>
            </ul>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-border text-[10px] font-mono-plex uppercase tracking-widest text-muted-foreground flex flex-wrap items-center justify-between gap-3">
          <div>© 2026 RuleAI.Systems &middot; Pennsylvania, United States</div>
          <div>All rights reserved &middot; v1.0 beta</div>
        </div>
      </div>
    </footer>
  );
}
